/**
 * Contact service for managing contact form API calls
 * Provides functions to fetch user details, location data, and send contact messages
 * @module services/contactService
 */

import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import {
  ContactDetails,
  ContactLocation,
  ContactFormData,
  ContactSendResponse,
  LoadValueType,
} from "../../types/contact.types";

/**
 * Fetches contact details (NUID, first name, last name, email)
 * @returns Promise with contact details
 * @throws {Error} If API request fails
 * @example
 * const details = await fetchContactDetails();
 * console.log(details.nuid, details.firstName);
 */
export const fetchContactDetails = async (): Promise<ContactDetails> => {
  const response = await api.get<ContactDetails>(endpoints.ubt.contact.getContactDetails);
  return response.data;
};

/**
 * Fetches contact locations based on loadValue
 * @param loadValue - Type of data to load (CENTER or FEEDBACK_TYPE)
 * @param regionCode - Optional region code for filtering centers (required for CENTER)
 * @param centerCode - Optional center code for filtering feedback types (required for FEEDBACK_TYPE)
 * @returns Promise with location data array
 * @throws {Error} If API request fails
 * @example
 * // Fetch centers for a region
 * const centers = await fetchContactLocations("CENTER", "NW");
 * // Fetch topics for a center
 * const topics = await fetchContactLocations("FEEDBACK_TYPE", "NW", "SEA");
 */
export const fetchContactLocations = async (
  loadValue: LoadValueType,
  regionCode?: string,
  centerCode?: string
): Promise<ContactLocation[]> => {
  const response = await api.get<ContactLocation[]>(
    endpoints.ubt.contact.getContactLocations(loadValue, regionCode, centerCode)
  );
  return response.data;
};

/**
 * Sends contact us message
 * @param formData - Contact form data including user details and message
 * @returns Promise with send response containing success status and message
 * @throws {Error} If API request fails or validation errors occur
 * @example
 * const formData = {
 *   nuid: "12345",
 *   firstName: "John",
 *   lastName: "Doe",
 *   email: "john.doe@example.com",
 *   regionCode: "NW",
 *   regionDesc: "Northwest",
 *   centerCode: "SEA",
 *   centerDesc: "Seattle",
 *   phoneAreaCode: "206",  // Optional - only included if provided
 *   phone: "5551234",      // Optional - only included if provided
 *   ubtNameId: "Team Alpha",
 *   feedbackType: "Question",
 *   comments: "I have a question about UBT."
 * };
 * const response = await sendContactMessage(formData);
 */
export const sendContactMessage = async (
  formData: ContactFormData
): Promise<ContactSendResponse> => {
  const response = await api.post<ContactSendResponse>(
    endpoints.ubt.contact.sendContactMessage,
    formData
  );
  return response.data;
};

